import { PhotographIcon, XCircleIcon } from "@heroicons/react/outline";
import { useCreate, useTranslation } from "@refinedev/core";
import { Loader } from "components/loader";
import { IUploadFile, IUploadImageData } from "interfaces";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { bytesForHuman, isInvalidImageSize } from "utills/helpers";

interface DropzoneProps {
  id?: string;
  value?: string;
  customAccept?: any;
  customInvalidTypeMessage?: string;
  disabled?: boolean;
  validator?: {
    width?: number;
    height?: number;
  };
  [x: string]: any;
}
const Dropzone = React.forwardRef(
  ({ onChange, validator, customAccept, customInvalidTypeMessage, value, disabled = false }: DropzoneProps, ref: any) => {
    const { translate } = useTranslation();
    const { mutate, isLoading: isLoading } = useCreate();
    const [imageData, setImageData] = useState<IUploadImageData>({
      url: value ?? "",
      files: [],
    });

    const _uploadImage = async (file: File) => {
      const formData = new FormData();
      formData.append("upload", file, `${file.name}`);
      return new Promise((resolve, reject) => {
        mutate(
          {
            dataProviderName: "externalUpload",
            resource: "/",
            values: formData,
          },
          {
            onSuccess: (data) => {
              const imageUrl = data.data?.result.img_url;
              resolve(imageUrl || "");
            },
          }
        );
      });
    };

    const {
      getRootProps: getRootWatermarkProps,
      getInputProps: getInputWatermarkProps,
      open: openUploadWatermark,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: false,
      accept: customAccept ? customAccept : {
        "image/jpeg": [".jpeg", ".png", ".jpg"],
      },
      validator: (file: File) => {
        const maximumSize = 2097152; // 2 MB
        if (isInvalidImageSize(file, maximumSize)) {
          return {
            code: "invalid-size",
            message: `${translate("dropzone.invalidImageSize")}.\n ${translate("dropzone.maximumFileSize")}: ${bytesForHuman(
              maximumSize
            )}.`,
          };
        }
        return null;
      },
      onDrop: async (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          acceptedFiles.map((file) => {
            const image = new Image();
            image.addEventListener("load", async () => {
              if (
                !validator ||
                ((validator.width ? image.width === validator.width : true) &&
                  (validator.height ? image.height === validator.height : true))
              ) {
                const imageUrl: string = (await _uploadImage(
                  acceptedFiles[0]
                )) as string;
                if (!imageUrl) return;

                const updatedFiles: IUploadFile[] = acceptedFiles.map((file) =>
                  Object.assign(file, {
                    preview: URL.createObjectURL(file),
                  })
                );
                const updatedImageData = {
                  url: imageUrl,
                  files: updatedFiles,
                };
                onChange(updatedImageData.url);
                return setImageData(updatedImageData);
              }
              let errorMessage = "";
              if (validator.width && validator.height) {
                errorMessage = `${translate("dropzone.invalidImageDimensions")}.\n${translate("dropzone.requiredDimensions")}: ${validator.width} x ${validator.height}.`
              } else {
                if (validator.width) {
                  errorMessage = `${translate("dropzone.invalidImageDimensions")}.\n${translate("dropzone.requiredWidthSize")}: ${validator.width}.`
                } 
                if (validator.height) {
                  errorMessage = `${translate("dropzone.invalidImageDimensions")}.\n${translate("dropzone.requiredHeightSize")}: ${validator.width}.`
                }
              }
              return toast.error(
                errorMessage
              );
            });
            image.addEventListener("error", () => {
              return toast.error("Unable to load image");
            });

            image.src = URL.createObjectURL(file);
            return null;
          });
        }
      },
      onDropRejected: (errorFiles) => {
        if (!isEmpty(errorFiles)) {
          if (errorFiles[0].errors[0].code === "file-invalid-type") {
            return toast.error(
              customInvalidTypeMessage ?? translate("dropzone.supportedFormat", {
                format: "PNG, JPG, JPEG"
              })
            );
          }
          return toast.error(errorFiles[0].errors[0].message);
        }
      },
    });

    const _clearImageData = () => {
      setImageData({
        url: "",
        files: [],
      });
      onChange("");
    };

    const watermarkThumbs = imageData.files.map((file: IUploadFile) => (
      <div key={file.name} className="w-full h-full">
        <img
          src={file.preview}
          onLoad={() => {
            URL.revokeObjectURL(file.preview);
          }}
          className="w-auto h-full bg-gray-200"
          alt="preview"
        />
      </div>
    ));

    return !imageData.url && imageData.files.length === 0 ? (
      <section className="w-full h-full">
        <div {...getRootWatermarkProps({ className: "h-full" })}>
          <div
            className="
            relative overflow-hidden
            flex flex-col justify-center items-center 
            rounded-lg 
            border border-dashed border-gray-900/25 bg-gray-50
            px-6 py-8 
            h-60 
            text-center
          "
          >
            {isLoading ? (
              <div
                className="
                opacity-100
              bg-gray-900/40
                duration-300
                absolute
                inset-0
                flex
                justify-center
                items-center
                rounded-lg
              "
              >
                <Loader size={40} />
              </div>
            ) : null}

            <div>
              <PhotographIcon
                className="h-12 w-12 text-gray-300 mx-auto"
                aria-hidden="true"
              />
              {!disabled ? <input {...getInputWatermarkProps()} /> : <></>}
              <div className="mt-4">
                <p>{translate("dropzone.dragAndDropImage")}</p>
                <button
                  type="button"
                  onClick={openUploadWatermark}
                  className="font-semibold text-main-blue-600 focus-within:outline-none focus-within:ring-2 hover:text-main-bllue-500 focus:ring-transparent"
                >
                  {translate("dropzone.browseImage")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    ) : (
      <div
        className="
        flex flex-col justify-center items-center 
        rounded-lg 
        border border-solid border-gray-900/25
        px-4 lg:px-6
        py-6
        text-center
        w-auto
        h-60
      "
      >
        <div className="overflow-hidden relative w-fit mx-auto group cursor-pointer">
          {!disabled ? (
            <div
              className="
              opacity-0
              bg-transparent
              group-hover:opacity-100
              group-hover:bg-gray-900/40
              duration-300
              absolute
              inset-0
              flex
              justify-center
              items-center
            "
              onClick={_clearImageData}
            >
              <XCircleIcon
                width={40}
                height={40}
                color="#ffffff"
                className="cursor-pointer"
              />
            </div>
          ) : <></>}
          {imageData.url ? (
            <img
              src={imageData.url}
              className="w-auto h-full bg-gray-200"
              alt="banner"
            />
          ) : (
            watermarkThumbs
          )}
        </div>
      </div>
    );
  }
);

Dropzone.displayName = "Dropzone";

export default Dropzone;
